import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Scale,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Shield,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import TrustScoreBadge from "@/components/profile/TrustScoreBadge";

interface BanAppeal {
  id: string;
  ban_id: string;
  user_id: string;
  reason: string;
  status: string;
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  user?: {
    name: string;
    photo_url: string | null;
  };
  ban?: {
    ban_type: string;
    reason: string;
    expires_at: string | null;
    is_permanent: boolean;
    created_at: string;
  };
}

const AdminAppealsPanel: React.FC = () => {
  const { user } = useAuthContext();
  const [appeals, setAppeals] = useState<BanAppeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reviewModal, setReviewModal] = useState<BanAppeal | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  const fetchAppeals = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("ban_appeals")
        .select(`
          *,
          user:profiles!ban_appeals_user_id_fkey(name, photo_url),
          ban:user_bans!ban_appeals_ban_id_fkey(ban_type, reason, expires_at, is_permanent, created_at)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setAppeals(data || []);
    } catch (error) {
      console.error("Error fetching appeals:", error);
      toast({
        title: "Error",
        description: "Failed to fetch appeals",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAppeals();
  }, [fetchAppeals]);

  const handleReview = async (appealId: string, approved: boolean) => {
    if (!user) return;

    setProcessing(true);
    try {
      // Update appeal status
      const { error: appealError } = await supabase
        .from("ban_appeals")
        .update({
          status: approved ? "approved" : "rejected",
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          admin_notes: adminNotes || null,
        })
        .eq("id", appealId);

      if (appealError) throw appealError;

      // If approved, lift the ban
      if (approved && reviewModal?.ban_id) {
        const { error: banError } = await supabase
          .from("user_bans")
          .update({
            is_active: false,
            lifted_at: new Date().toISOString(),
            lifted_by: user.id,
          })
          .eq("id", reviewModal.ban_id);

        if (banError) throw banError;

        // Notify user their ban was lifted
        await supabase.from("notifications").insert({
          user_id: reviewModal.user_id,
          type: "appeal_approved",
          title: "✅ Appeal Approved",
          body: "Your ban appeal has been approved and your access has been restored.",
        });
      } else if (!approved && reviewModal?.user_id) {
        // Notify user their appeal was rejected
        await supabase.from("notifications").insert({
          user_id: reviewModal.user_id,
          type: "appeal_rejected",
          title: "❌ Appeal Rejected",
          body: "Your ban appeal has been reviewed and rejected. The ban remains in effect.",
        });
      }

      toast({
        title: approved ? "✅ Appeal Approved" : "❌ Appeal Rejected",
        description: approved
          ? "The user's ban has been lifted"
          : "The appeal has been rejected",
      });

      setReviewModal(null);
      setAdminNotes("");
      fetchAppeals();
    } catch (error) {
      console.error("Error processing appeal:", error);
      toast({
        title: "Error",
        description: "Failed to process appeal",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const pendingCount = appeals.filter((a) => a.status === "pending").length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Scale className="w-6 h-6 text-blue-500" />
          <h2 className="text-xl font-bold">Ban Appeals</h2>
          {pendingCount > 0 && (
            <Badge className="bg-blue-500 text-white">{pendingCount} pending</Badge>
          )}
        </div>
        <Button variant="outline" size="icon" onClick={fetchAppeals}>
          <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
        </Button>
      </div>

      {/* Appeals List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : appeals.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Scale className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No appeals to review</p>
        </div>
      ) : (
        <div className="space-y-3">
          {appeals.map((appeal) => (
            <div
              key={appeal.id}
              className={cn(
                "border rounded-xl transition-all",
                appeal.status === "pending" ? "bg-card" : "bg-muted/30",
                expandedId === appeal.id && "ring-2 ring-primary"
              )}
            >
              {/* Appeal Row */}
              <div className="flex items-center gap-3 p-3">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={appeal.user?.photo_url || undefined} />
                  <AvatarFallback>{appeal.user?.name?.charAt(0) || "?"}</AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{appeal.user?.name || "Unknown"}</span>
                    <TrustScoreBadge userId={appeal.user_id} size="sm" />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{format(new Date(appeal.created_at), "MMM d, h:mm a")}</span>
                    <span>•</span>
                    <span className="truncate">{appeal.ban?.ban_type || "Ban"}</span>
                  </div>
                </div>

                {/* Status */}
                {appeal.status === "pending" ? (
                  <Badge className="bg-amber-100 text-amber-700">
                    <Clock className="w-3 h-3 mr-1" />
                    Pending
                  </Badge>
                ) : appeal.status === "approved" ? (
                  <Badge className="bg-green-100 text-green-600">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Approved
                  </Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-600">
                    <XCircle className="w-3 h-3 mr-1" />
                    Rejected
                  </Badge>
                )}

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setExpandedId(expandedId === appeal.id ? null : appeal.id)}
                  >
                    {expandedId === appeal.id ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Button>
                  {appeal.status === "pending" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setReviewModal(appeal)}
                    >
                      Review
                    </Button>
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === appeal.id && (
                <div className="px-4 pb-4 pt-2 border-t space-y-3">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Appeal Reason</p>
                    <p className="text-sm bg-muted p-2 rounded-lg">{appeal.reason}</p>
                  </div>
                  
                  {appeal.ban && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-medium text-red-700 dark:text-red-300">
                          Ban Details
                        </span>
                      </div>
                      <div className="text-xs space-y-1 text-red-600 dark:text-red-400">
                        <p>Type: {appeal.ban.ban_type}</p>
                        <p>Reason: {appeal.ban.reason}</p>
                        <p>
                          Duration:{" "}
                          {appeal.ban.is_permanent
                            ? "Permanent"
                            : appeal.ban.expires_at
                            ? `Until ${format(new Date(appeal.ban.expires_at), "MMM d, yyyy")}`
                            : "Unknown"}
                        </p>
                      </div>
                    </div>
                  )}

                  {appeal.admin_notes && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Admin Notes</p>
                      <p className="text-sm bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg text-blue-800 dark:text-blue-200">
                        {appeal.admin_notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      <Dialog open={!!reviewModal} onOpenChange={(open) => !open && setReviewModal(null)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-blue-500" />
              Review Appeal
            </DialogTitle>
            <DialogDescription>
              Review the appeal from {reviewModal?.user?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Appeal Details */}
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs font-medium text-muted-foreground mb-1">Appeal Reason</p>
              <p className="text-sm">{reviewModal?.reason}</p>
            </div>

            {/* Ban Details */}
            {reviewModal?.ban && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-xs font-medium text-red-600 mb-1">Original Ban</p>
                <p className="text-sm text-red-700 dark:text-red-300">{reviewModal.ban.reason}</p>
              </div>
            )}

            {/* Admin Notes */}
            <div>
              <label className="text-sm font-medium mb-2 block">Admin Notes (optional)</label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add notes about your decision..."
                rows={3}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => reviewModal && handleReview(reviewModal.id, false)}
                disabled={processing}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => reviewModal && handleReview(reviewModal.id, true)}
                disabled={processing}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve & Lift Ban
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAppealsPanel;
